import * as React from 'react';
import { useState, useEffect } from 'react';
import { 
  AppBar, Box, Toolbar, IconButton, Typography, Menu, MenuItem, 
  Button, Drawer, List, ListItem, ListItemText, Divider 
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import LogoImage from '../../assets/logo.webp';
import { auth, db } from '../../config/firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import Stack from '@mui/material/Stack';
import Rectangle19 from '../../assets/Rectangle 19.webp';
import { collection, query, where, getDocs } from "firebase/firestore";

const pages = ['Pricing', 'Token', 'Test'];
const mobilepages = ['Pricing', 'History','Token', 'Educator'];

function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorElModels, setAnchorElModels] = useState(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [userName, setUsername] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true; // Prevents state updates if the component unmounts

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        if (isMounted) setLoggedIn(true);
        
        try {
          const usersCollectionRef = collection(db, "users");
          const q = query(usersCollectionRef, where("uid", "==", user.uid));
          const querySnapshot = await getDocs(q);

          if (isMounted) {
            if (!querySnapshot.empty) {
              // Directly access the first document instead of looping
              setUsername(querySnapshot.docs[0].data().username);
            } else if (user.displayName) {
              // Fallback for Google Sign-In users before their DB record is fully set up
              setUsername(user.displayName);
            }
          }
        } catch (error) {
          console.error("Error fetching user documents:", error);
        }
      } else {
        if (isMounted) {
          setLoggedIn(false);
          setUsername('');
        }
      }
    });

    return () => {
      isMounted = false; // Cleanup function
      unsubscribe();
    };
  }, []);

  const handleOpenModelsMenu = (event) => {
    setAnchorElModels(event.currentTarget);
  };

  const handleCloseModelsMenu = () => {
    setAnchorElModels(null);
  };

  const handleModelSelect = (model) => {
    navigate(`/${model}`);
    handleCloseModelsMenu();
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setLoggedIn(false);
      setUsername('');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const appBarStyle = {
    backgroundColor: 'black',
    backgroundSize: 'cover',
    justifyContent: 'right',
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const drawerList = (
    <Box
      sx={{ 
        width: 250,
        bgcolor: 'black', 
        color: 'white', 
        height: '100%', 
        '& .MuiListItem-root': { 
          borderBottom: '1px solid white', 
          '&:last-child': { 
            borderBottom: 'none',
          },
        },
        '& .MuiListItemText-primary': { 
          color: 'white',
        },
        '& .MuiDivider-root': { 
          borderColor: 'white',
        }
      }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        <ListItem button component={Link} to="/">
          <ListItemText primary="Home" />
        </ListItem>
        {mobilepages.map((page) => (
          <ListItem button key={page} component={Link} to={`/${page.toLowerCase()}`}>
            <ListItemText primary={page} />
          </ListItem>
        ))}
        {loggedIn && (
          <ListItem button onClick={() => handleModelSelect('essay-detector')}>
            <ListItemText primary="Models" />
          </ListItem>
        )}
        <Divider />
        {loggedIn ? (
          <ListItem button onClick={handleLogout}>
            <ListItemText primary="Logout" />
          </ListItem>
        ) : (
          <ListItem button component={Link} to="/login">
            <ListItemText primary="Login" />
          </ListItem>
        )}
      </List>
    </Box>
  );  

  return (
    <AppBar position="static" sx={appBarStyle}>
      <Toolbar disableGutters>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={toggleDrawer(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="left"
              open={drawerOpen}
              onClose={toggleDrawer(false)}
            >
              {drawerList}
            </Drawer>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            <img src={LogoImage} alt="Logo" style={{ width: '54px', height: '54px', marginRight: '10px' }} />
            <Typography
              variant="h6"
              noWrap
              component={Link}
              to="/"
              sx={{
                mr: 2,
                display: { xs: 'none', md: 'flex' },
                fontFamily: 'monospace',
                fontWeight: 700,
                letterSpacing: '.3rem',
                color: 'inherit',
                textDecoration: 'none',
              }}
            >
              SYNTHETICA
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
          <Stack direction="row" spacing={5}>
            {pages.map((page) => (
              <Button
                key={page}
                component={Link}
                to={`/${page.toLowerCase()}`}
                sx={{ my: 2, color: 'white', display: 'block', textTransform: 'uppercase', fontSize: '14px', fontWeight: 600 }}
              >
                {page}
              </Button>
            ))}
            {loggedIn && (
              <Button
                onClick={() => handleModelSelect('essay-detector')}
                sx={{ my: 2, color: 'white', display: 'block', textTransform: 'uppercase', fontSize: '14px', fontWeight: 600 }}
              >
                Models
              </Button>
            )}
          </Stack>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        {loggedIn ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                position: 'relative',
                cursor: 'pointer',
                marginRight: '10px',
                className: 'ParentUsername'
              }}
            >
              <img src={Rectangle19} className='usernameimg' alt={userName} style={{ height: 'auto' }} />
              <Typography
                sx={{
                  position: 'absolute',
                  className: 'username',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                }}
              >
                {userName}
              </Typography>
            </Box>
            <Box
              sx={{
                position: 'relative',
                cursor: 'pointer',
                marginRight: '10px',
              }}
              onClick={handleLogout}
            >
              <img src={Rectangle19} alt="Logout" className='log' style={{height: 'auto' }} />
              <Typography
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                }}
              >
                Logout
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              position: 'relative',
              cursor: 'pointer',
              marginRight: '10px',
            }}
            component={Link}
            to="/login"
          >
            <img src={Rectangle19} alt="Login" style={{ width: '150px', height: 'auto' }} />
            <Typography
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'white',
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              Login
            </Typography>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}

export default Navbar;